import React, { useMemo, useState } from "react";
import { Bot, Brain, Lightbulb, Loader2, MessageSquare, Send, Sparkles, Wand2 } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Textarea } from "./ui/textarea";

const renderMarkdownLite = (content) => {
  const lines = String(content || "").split("\n").filter(Boolean);
  return lines.map((line, index) => {
    if (line.startsWith("- ")) {
      return (
        <li key={index} className="ml-4 list-disc">
          {line.slice(2)}
        </li>
      );
    }
    return (
      <p key={index} className="leading-6">
        {line}
      </p>
    );
  });
};

const AICopilotPanel = ({
  selectedProject,
  taskCount = 0,
  dashboardInsights = null,
  recommendations = [],
  onGenerateSprintPlan,
  onBreakdownGoal,
  onRewriteComment,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "I am ready with execution context. Ask me for sprint planning, blocker analysis, workload balancing, delivery risk reasoning, or a crisp action plan for leadership review.",
    },
  ]);

  const suggestions = useMemo(
    () => [
      { label: "Plan sprint", prompt: `Create a sprint plan for ${selectedProject?.name || "this project"}` },
      { label: "Find blockers", prompt: "Detect blockers and delivery risks in the current work" },
      { label: "Prioritize", prompt: "Prioritize this backlog and explain the reasoning" },
      { label: "Break down goal", prompt: "Break down goal: Launch onboarding improvements" },
    ],
    [selectedProject?.name]
  );

  const contextBullets = useMemo(() => {
    const risk = dashboardInsights?.riskLevel || "Unknown";
    const completion = dashboardInsights?.completionRate ?? 0;
    return [
      `Project: ${selectedProject?.name || "No project selected"}`,
      `Tasks: ${taskCount}`,
      `Completion: ${completion}%`,
      `Risk: ${risk}`,
    ];
  }, [dashboardInsights, selectedProject?.name, taskCount]);

  const addAssistantMessage = (text) => {
    setMessages((prev) => [...prev, { role: "assistant", content: text }]);
  };

  const addUserMessage = (text) => {
    setMessages((prev) => [...prev, { role: "user", content: text }]);
  };

  const runSprintPlan = async () => {
    addUserMessage("Generate a sprint plan.");
    await onGenerateSprintPlan?.();
    addAssistantMessage(
      "Sprint plan generated.\n- Reasoning: I used current backlog shape, completion signal, and project context to structure the plan.\n- Operational impact: review capacity before committing the sprint externally.\n- Recommendation: use the Sprint tab as the working plan and capture any de-scope decisions in the knowledge base.\n- Confidence: directional until lead review and owner availability are confirmed."
    );
  };

  const inferAction = (value) => {
    const text = value.toLowerCase();
    if (text.includes("sprint") || text.includes("plan")) return "sprint";
    if (text.includes("rewrite") || text.includes("comment")) return "rewrite";
    if (text.includes("break") || text.includes("goal") || text.includes("task")) return "breakdown";
    return "analysis";
  }

  const runPrompt = async (value = prompt) => {
    const nextPrompt = value.trim();
    if (!nextPrompt) return;

    addUserMessage(nextPrompt);
    setPrompt("");

    const action = inferAction(nextPrompt);
    if (action === "sprint") {
      await onGenerateSprintPlan?.();
      addAssistantMessage("I generated a sprint plan from the current backlog.\n- Reasoning: sprint scope is organized around available task signals and priority pressure.\n- Operational impact: this gives the team a starting commitment model, not a final forecast.\n- Recommendation: confirm owners, dependencies, and review capacity before presenting it as committed delivery.\n- Confidence: medium until velocity and approval cadence are validated.");
      return;
    }
    if (action === "rewrite") {
      await onRewriteComment?.(nextPrompt.replace(/^rewrite comment:\s*/i, ""));
      addAssistantMessage("I rewrote that into a structured execution item.\n- Reasoning: vague comments create follow-up ambiguity, so I converted it into owner, priority, deadline, and acceptance criteria fields.\n- Operational impact: the team can now track the request as accountable work.\n- Recommendation: confirm the owner before using it for sprint commitment.");
      return;
    }
    if (action === "breakdown") {
      await onBreakdownGoal?.(nextPrompt.replace(/^break down goal:\s*/i, ""));
      addAssistantMessage("I decomposed the goal into executable tasks.\n- Reasoning: delivery improves when broad goals are converted into small, owned, sequenced work.\n- Operational impact: the Breakdown tab can now seed backlog planning and dependency discussion.\n- Recommendation: add only the tasks that have clear ownership and acceptance criteria.");
      return;
    }

    const recommendationText = recommendations
      .map((item) => `- ${item.title}: ${item.description}`)
      .join("\n");
    const risk = dashboardInsights?.riskLevel || "Unknown";
    const completion = dashboardInsights?.completionRate ?? 0;
    const overdue = dashboardInsights?.overdueTasks ?? 0;
    addAssistantMessage(
      `Execution readout for ${selectedProject?.name || "the current workspace"}:\n${recommendationText || "- No critical risk signals found right now."}\n- Reasoning: risk is ${risk.toLowerCase()} with ${completion}% completion and ${overdue} overdue item(s).\n- Operational impact: these signals affect delivery confidence, stakeholder reporting, and whether new scope should be accepted.\n- Recommendation: clear review bottlenecks and ownership gaps before expanding sprint scope.\n- Confidence: directional because this readout uses live task status and project context, not a full historical velocity model.`
    );
  };

  return (
    <div className="fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] right-4 z-40 sm:right-5 md:bottom-5">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button className="h-11 rounded-full px-4 shadow-glow sm:h-10">
            <Bot className="mr-2 h-4 w-4" />
            AI Copilot
          </Button>
        </SheetTrigger>
        <SheetContent className="flex w-[94vw] max-w-2xl flex-col overflow-hidden p-0">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 border-b px-5 py-4">
              <Sparkles className="h-4 w-4 text-indigo-500" />
              ProjectFlow Copilot
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
            <Card className="bg-muted/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Context</CardTitle>
                <CardDescription>
                  Live workspace signals used for recommendations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {contextBullets.map((item) => (
                    <div key={item} className="rounded-lg border bg-background/70 px-3 py-2 text-xs text-muted-foreground">
                      {item}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Wand2 className="h-4 w-4 text-violet-500" />
                  Suggested Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.label}
                      type="button"
                      onClick={() => runPrompt(suggestion.prompt)}
                      className="rounded-full border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                      disabled={loading}
                    >
                      {suggestion.label}
                    </button>
                  ))}
                  <Button className="rounded-full" size="sm" onClick={runSprintPlan} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    Sprint
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {messages.map((message, idx) => (
                <div
                  key={`${message.role}-${idx}`}
                  className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[88%] rounded-2xl border px-3 py-2 text-sm shadow-sm ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-card text-card-foreground"
                    }`}
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs opacity-80">
                      {message.role === "user" ? <MessageSquare className="h-3 w-3" /> : <Brain className="h-3 w-3" />}
                      <Badge variant="outline" className="h-5 rounded-full px-2 text-[10px] capitalize">
                        {message.role}
                      </Badge>
                    </div>
                    <div className="space-y-1">{renderMarkdownLite(message.content)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t bg-background/95 p-4">
            <form
              className="space-y-2"
              onSubmit={(event) => {
                event.preventDefault();
                runPrompt();
              }}
            >
              <Textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Ask about risks, priorities, sprint planning, or rewrite a comment..."
                className="min-h-[84px] resize-none"
              />
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Lightbulb className="h-3.5 w-3.5" />
                  Explains reasoning using current workspace signals.
                </div>
                <Button type="submit" disabled={loading || !prompt.trim()}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send
                </Button>
              </div>
            </form>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AICopilotPanel;
